import React from 'react';
import { cx } from '@milanote/roma/utils';
import { LoadingIndicator } from '@milanote/roma';

import { useTranslator } from '../../../../../components/i18n/useTranslator';
import Toast from '../../../../../structural/toast/Toast';
import {
    IMAGE_MODAL_V2_OPERATION_MESSAGE,
    type ImageModalV2OperationToastState,
} from './imageModalV2OperationToastTypes';
import { IMAGE_MODAL_V2_TOAST_EXIT_FALLBACK_MS } from '../imageModalV2Constants';
import { useImageModalV2SurfaceLifecycle } from './useImageModalV2SurfaceLifecycle';

import './ImageModalV2OperationToast.scss';

/**
 * Modal-owned operation toast (background removal, upload, save milestones) whose enter/exit
 * participates in the bottom-surface handoff. The caller decides the exit handoff hold via
 * `exitHandoffMs`, since it is the one that knows the semantics of each `toast.message`.
 */
const ImageModalV2OperationToast: React.FC<{
    toast: ImageModalV2OperationToastState;
    isOpen: boolean;
    /** Extra hold before `onExitComplete` fires once this toast has finished exiting. Defaults to none. */
    exitHandoffMs?: number;
    onExitComplete: () => void;
    onEnterComplete?: () => void;
}> = ({ toast, isOpen, exitHandoffMs, onExitComplete, onEnterComplete }) => {
    const translate = useTranslator('popup', 'imageModal.backgroundRemovalToast');
    const { isShown, hasEntered, handleTransitionEnd } = useImageModalV2SurfaceLifecycle({
        isOpen,
        fallbackMs: IMAGE_MODAL_V2_TOAST_EXIT_FALLBACK_MS,
        exitHandoffMs,
        onExitComplete,
        onEnterComplete,
    });

    // The usage snapshot is captured before the removal starts and the server only counts a
    // removal once the provider call succeeds, so the count excludes the in-flight removal —
    // display it as the next slot.
    const usage = toast.usage ?? null;
    const removingBackgroundLabel =
        usage && usage.limit !== null
            ? translate('removingBackgroundWithUsage', {
                  used: Math.min(usage.count + 1, usage.limit),
                  total: usage.limit,
              })
            : translate('removingBackground');

    const content = (() => {
        switch (toast.message) {
            case IMAGE_MODAL_V2_OPERATION_MESSAGE.REMOVING_BACKGROUND:
                return <LoadingIndicator size="xs">{removingBackgroundLabel}</LoadingIndicator>;
            case IMAGE_MODAL_V2_OPERATION_MESSAGE.SAVING_CHANGES:
                return <LoadingIndicator size="xs">{translate('savingChanges')}</LoadingIndicator>;
        }
    })();

    return (
        <Toast
            className={cx('ImageModalV2OperationToast', isShown && 'is-shown', hasEntered && 'has-entered')}
            groupId="ImageModalV2"
            ownsMotion
            onTransitionEnd={handleTransitionEnd}
        >
            {content}
        </Toast>
    );
};

export default ImageModalV2OperationToast;
